import {
  useState, useEffect, useRef
} from "react";
import {
  API_BASE_URL
} from '../config';
import {
  trackEvent
} from '../analytics/posthog';
import {
  validateNewInstanceLabel
} from '../utils/integrationLabels';
import {
  AccountNameField
} from './AccountNameField';
import {
  PairingModalShell
} from './pairing/PairingModalShell';
import {
  isMobileBrowser
} from '../utils/inAppBrowser';

// The backend serves the OAuth callback HTML from `API_BASE_URL`, which posts a
// `${name}-oauth` message back to this window. Only accept messages from that
// origin — any other window able to reach the opener could otherwise forge a
// "connected" signal. (The popup-closed status re-check below is the second
// line of defence.)
const EXPECTED_CALLBACK_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return null;
  }
})();

interface OAuthIntegrationEvents {
  modalOpened: string;
  oauthStarted: string;
  connected: string;
  connectionFailed: string;
  modalClosed: string;
}

interface OAuthIntegrationService {
  getAuthUrl: (
    label?: string,
    returnMode?: 'popup' | 'redirect',
  ) => Promise<{ authorization_url: string }>;
  getStatus: () => Promise<{ connected: boolean }>;
}

// Tailwind requires complete class strings for purging — dynamic interpolation
// like `bg-${color}-600` is invisible to the scanner.
const COLOR_CLASSES = {
  purple: {
    featureBg: 'bg-purple-50',
    featureBorder: 'border-purple-200',
    featureTitle: 'text-purple-900',
    featureText: 'text-purple-800',
    button: 'bg-purple-600 hover:bg-purple-700',
  },
  violet: {
    featureBg: 'bg-violet-50',
    featureBorder: 'border-violet-200',
    featureTitle: 'text-violet-900',
    featureText: 'text-violet-800',
    button: 'bg-violet-600 hover:bg-violet-700',
  },
  slate: {
    featureBg: 'bg-slate-50',
    featureBorder: 'border-slate-200',
    featureTitle: 'text-slate-900',
    featureText: 'text-slate-800',
    button: 'bg-slate-800 hover:bg-slate-900',
  },
  orange: {
    featureBg: 'bg-orange-50',
    featureBorder: 'border-orange-200',
    featureTitle: 'text-orange-900',
    featureText: 'text-orange-800',
    button: 'bg-orange-600 hover:bg-orange-700',
  },
  sky: {
    featureBg: 'bg-sky-50',
    featureBorder: 'border-sky-200',
    featureTitle: 'text-sky-900',
    featureText: 'text-sky-800',
    button: 'bg-sky-600 hover:bg-sky-700',
  },
} as const;

type AccentColor = keyof typeof COLOR_CLASSES;

// Paints a lightweight loading page into the blank popup we open synchronously
// on click, so the user sees a spinner instead of a blank white window while we
// fetch the OAuth URL from the backend. Replaced by the real OAuth page once we
// navigate the popup. The popup is same-origin (about:blank inherits the
// opener's origin), so document access is allowed; displayName is escaped even
// though it's an internal constant.
function writePopupLoadingState(popup: Window, displayName: string) {
  const safeName = displayName.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c)
  );
  popup.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Connecting…</title>
<style>
  body { margin: 0; height: 100vh; display: flex; align-items: center; justify-content: center;
         font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
         background: #ffffff; color: #4b5563; }
  .box { text-align: center; }
  .spinner { width: 42px; height: 42px; margin: 0 auto 18px; border-radius: 50%;
             border: 4px solid #e5e7eb; border-top-color: #4f46e5;
             animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .label { font-size: 14px; }
</style>
</head>
<body>
  <div class="box">
    <div class="spinner"></div>
    <div class="label">Connecting to ${safeName}…</div>
  </div>
</body>
</html>`);
  popup.document.close();
}

interface OAuthIntegrationPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  name: string;
  displayName: string;
  description: string;
  features: string[];
  accentColor: AccentColor;
  events: OAuthIntegrationEvents;
  service: OAuthIntegrationService;
  // Labels of accounts already connected for this integration. When non-empty,
  // this is an "add another account" flow: a required, unique label is
  // collected and passed to the OAuth start so a second instance is created
  // instead of overwriting the first.
  existingLabels?: string[] | undefined;
}

export function OAuthIntegrationPairingModal({
  isOpen,
  onClose,
  onSuccess,
  name,
  displayName,
  description,
  features,
  accentColor,
  events,
  service,
  existingLabels = [],
}: OAuthIntegrationPairingModalProps) {
  const colors = COLOR_CLASSES[accentColor];
  const isAddingAnother = existingLabels.length > 0;
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const modalOpenTracked = useRef(false);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      trackEvent(events.modalOpened);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
      setError(null);
      setConnecting(false);
      setLabel('');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // A closed modal has no connect in flight, so anything arriving now was
      // started by something else — since GET-11379 the suggestions row
      // connects in-house integrations itself, and its callback posts the same
      // `${integration}-oauth` type this listens for. Ten of these modals are
      // mounted on the settings page at once, and the reset above only runs on
      // an `isOpen` transition, so a failure would otherwise leave a stale
      // error waiting inside a modal the user never opened.
      if (!isOpen) return;
      if (
        EXPECTED_CALLBACK_ORIGIN !== null
        && event.origin !== EXPECTED_CALLBACK_ORIGIN
      ) {
        // Posted from somewhere other than our backend callback — ignore.
        return;
      }
      if (event.data?.type === `${name}-oauth`) {
        if (event.data.success) {
          handleSuccess();
        } else {
          const errorMsg = event.data.message?.replace('error:', '') || 'Connection failed';
          setError(errorMsg);
          setConnecting(false);
          trackEvent(events.connectionFailed, { error: errorMsg });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // `isOpen` is read inside the handler, so the listener has to be re-bound
    // when it changes or the closed-modal guard above would test a stale value.
  }, [isOpen]);

  const handleConnect = async () => {
    const trimmedLabel = label.trim();
    if (isAddingAnother) {
      const labelError = validateNewInstanceLabel(existingLabels, label);
      if (labelError) {
        setError(labelError);
        return;
      }
    }

    // Shared failure tail for both connect paths: the auth-URL fetch threw
    // before we ever reached the provider.
    const reportConnectStartError = (err: any) => {
      const errorMessage =
        err.response?.data?.detail || `Failed to start ${displayName} connection`;
      trackEvent(events.connectionFailed, { error: errorMessage });
      setError(errorMessage);
      setConnecting(false);
    };

    // Mobile browsers (iOS Safari especially) can't complete the popup OAuth
    // handshake: the blank popup opens as a detached tab, window.opener is
    // severed, and window.close() no-ops, so the callback never reports back
    // and the user is stuck on the spinner (GET-8963). Navigate the top-level
    // tab to the auth URL instead; the backend callback 302s us back to
    // /integrations, where SettingsPage reads the result on the fresh mount.
    // Unlike the popup path, a full-page navigation isn't gated by the popup
    // blocker, so awaiting the auth URL first is safe here.
    if (isMobileBrowser()) {
      setConnecting(true);
      setError(null);
      try {
        trackEvent(events.oauthStarted);
        const { authorization_url } = await service.getAuthUrl(
          trimmedLabel || undefined,
          'redirect'
        );
        window.location.assign(authorization_url);
      } catch (err: any) {
        reportConnectStartError(err);
      }
      return;
    }

    // Open the popup *synchronously* inside the click gesture. The OAuth URL
    // isn't known yet — it comes from an async backend call, and in the "add
    // another account" flow it also depends on the label typed above — so it
    // can't be pre-fetched before the click. We open a blank window now and
    // navigate it once the URL resolves. Browsers only let window.open() bypass
    // the popup blocker when it runs synchronously in the user gesture;
    // awaiting getAuthUrl() first (as we used to) breaks that chain, so
    // Chrome/Safari blocked the resulting window.open and users saw "Popup was
    // blocked".
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      '',
      `${name}-oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup) {
      setError('Popup was blocked. Please allow popups and try again.');
      return;
    }

    popupRef.current = popup;
    writePopupLoadingState(popup, displayName);
    setConnecting(true);
    setError(null);

    try {
      trackEvent(events.oauthStarted);

      const { authorization_url } = await service.getAuthUrl(
        trimmedLabel || undefined
      );

      popup.location.href = authorization_url;

      pollIntervalRef.current = window.setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          setConnecting(false);

          service.getStatus().then((status) => {
            if (status.connected) {
              handleSuccess();
            }
          }).catch(() => {
            // Ignore errors, user might just have closed the popup
          });
        }
      }, 500);

    } catch (err: any) {
      // The blank popup is already open; close it since we never got a URL.
      popup.close();
      popupRef.current = null;
      reportConnectStartError(err);
    }
  };

  const handleClose = () => {
    trackEvent(events.modalClosed);
    setError(null);
    setConnecting(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    onClose();
  };

  const handleSuccess = () => {
    trackEvent(events.connected);
    setError(null);
    setConnecting(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    onClose();
    onSuccess();
  };

  return (
    <PairingModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={isAddingAnother ? `Add another ${displayName} account` : `Connect ${displayName}`}
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          <p className="mb-3">{description}</p>

          <div className={`${colors.featureBg} border ${colors.featureBorder} rounded-md p-3`}>
            <h4 className={`font-medium ${colors.featureTitle} mb-2`}>
              What you'll get:
            </h4>
            <ul className={`text-sm ${colors.featureText} space-y-1`}>
              {features.map((feature) => (
                <li key={feature}>• {feature}</li>
              ))}
            </ul>
          </div>
        </div>

        {isAddingAnother && (
          <AccountNameField
            id={`${name}-account-label`}
            value={label}
            onChange={setLabel}
            existingLabels={existingLabels}
            inputClassName="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
          />
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className={`px-4 py-2 text-sm font-medium text-white ${colors.button} disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center`}
          >
            {connecting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connecting...
              </>
            ) : isAddingAnother ? (
              'Add account'
            ) : (
              'Connect'
            )}
          </button>
        </div>
      </div>
    </PairingModalShell>
  );
}
